import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { type ReactNode, useEffect } from "react";
import { useForm } from "react-hook-form";

export interface FieldDef {
  key: string;
  label: string;
  readOnly?: boolean;
}

interface EditSheetProps {
  open: boolean;
  onClose: () => void;
  title: string;
  item: Record<string, unknown> | null;
  fields: FieldDef[];
  onSave: (data: Record<string, string>) => Promise<unknown>;
  isSaving?: boolean;
  onDelete?: () => Promise<unknown>;
  isDeleting?: boolean;
  extraActions?: ReactNode;
}

export function EditSheet({
  open,
  onClose,
  title,
  item,
  fields,
  onSave,
  isSaving,
  onDelete,
  isDeleting,
  extraActions,
}: EditSheetProps) {
  const { register, handleSubmit, reset } = useForm<Record<string, string>>();

  useEffect(() => {
    if (item) {
      const values: Record<string, string> = {};
      for (const f of fields) {
        values[f.key] = String(item[f.key] ?? "");
      }
      reset(values);
    } else {
      reset({});
    }
  }, [item, fields, reset]);

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="sm:max-w-md p-6">
        <SheetHeader>
          <SheetTitle>{title}</SheetTitle>
        </SheetHeader>
        <form
          onSubmit={handleSubmit(onSave)}
          className="mt-6 flex flex-col gap-4"
        >
          {fields.map((f) => (
            <div key={f.key} className="flex flex-col gap-1.5">
              <Label htmlFor={f.key}>{f.label}</Label>
              <Input
                id={f.key}
                {...register(f.key)}
                readOnly={f.readOnly}
                className={f.readOnly ? "bg-muted text-muted-foreground" : ""}
              />
            </div>
          ))}
          <div className="flex items-center gap-2 mt-2">
            <Button type="submit" disabled={isSaving || isDeleting} className="flex-1">
              {isSaving ? "Saving…" : "Save changes"}
            </Button>
            {onDelete && (
              <Button
                type="button"
                variant="destructive"
                disabled={isSaving || isDeleting}
                onClick={onDelete}
              >
                {isDeleting ? "Deleting…" : "Delete"}
              </Button>
            )}
          </div>
        </form>
        {extraActions && <div className="mt-6 border-t pt-4">{extraActions}</div>}
      </SheetContent>
    </Sheet>
  );
}
